import React, { useState, useEffect } from 'react';

// Interfaces
interface Dish {
  id: string;
  name: string;
  description: string;
  imagePath?: string;
  quantityLimit?: number;
}

interface Waiter {
  id: string;
  name: string;
}

interface EventBreak {
  id: string;
  name: string;
  dishes: Dish[];
}

interface AssignedWaiter {
  waiterId: string;
  waiterName: string;
  assignedTables: number[];
  pin: string;
}

interface EventData {
  name: string;
  estimatedGuests: number | null;
  activeTables: number[];
  reserveTables: number[];
  breaks: EventBreak[];
  assignedWaiters: AssignedWaiter[];
  scheduledFor: string | null;
}

type Step = 'basic' | 'tables' | 'breaks' | 'waiters' | 'review';

interface EventCreatorProps {
  onBack: () => void;
  onComplete: () => void;
  mode?: 'create' | 'edit';
  eventId?: string;
}

const EventCreator: React.FC<EventCreatorProps> = ({ onBack, onComplete, mode = 'create', eventId }) => {
  const [currentStep, setCurrentStep] = useState<Step>('basic');
  const [eventData, setEventData] = useState<EventData>({
    name: '',
    estimatedGuests: null,
    activeTables: [],
    reserveTables: [],
    breaks: [],
    assignedWaiters: [],
    scheduledFor: null
  });
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);

  // Available data
  const [availableDishes, setAvailableDishes] = useState<Dish[]>([]);
  const [availableWaiters, setAvailableWaiters] = useState<Waiter[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Copy from previous event modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [pastEvents, setPastEvents] = useState<any[]>([]);
  const [isLoadingPastEvents, setIsLoadingPastEvents] = useState(false);

  // Tables step state (lifted from TablesStep to prevent focus loss)
  const [activeTableInput, setActiveTableInput] = useState('');
  const [reserveTableInput, setReserveTableInput] = useState('');

  // Breaks step state (lifted from BreaksStep to prevent focus loss)
  const [showAddBreak, setShowAddBreak] = useState(false);
  const [newBreakName, setNewBreakName] = useState('');

  // Waiters step state (lifted from WaitersStep to prevent focus loss)
  const [showAddWaiter, setShowAddWaiter] = useState(false);
  const [selectedWaiterId, setSelectedWaiterId] = useState('');
  const [selectedWaiterTables, setSelectedWaiterTables] = useState<number[]>([]);

  useEffect(() => {
    loadAvailableData();
    // Load event data if in edit mode
    if (mode === 'edit' && eventId) {
      loadEventData(eventId);
    }
  }, [mode, eventId]);

  const loadEventData = async (id: string) => {
    setIsLoadingEvent(true);
    try {
      const response = await fetch(`/api/v1/events/${id}`);
      const data = await response.json();

      if (data.success && data.event) {
        const event = data.event;

        // Parse reserve tables
        let reserveTables: number[] = [];
        try {
          reserveTables = event.reserveTables ? JSON.parse(event.reserveTables) : [];
        } catch {}

        // Calculate active tables: all tables from 1 to tableCount, minus reserve tables
        const totalTables = event.tableCount || 0;
        const allTables = Array.from({ length: totalTables }, (_, i) => i + 1);
        const activeTables = allTables.filter(t => !reserveTables.includes(t));

        // Parse breaks with dishes
        const breaks: EventBreak[] = event.breaks?.map((b: any) => ({
          id: b.id,
          name: b.name,
          dishes: b.dishes?.map((bd: any) => bd.dish).filter(Boolean) || []
        })) || [];

        // Parse waiters - assignedTables is stored as JSON string
        const assignedWaiters: AssignedWaiter[] = event.waiters?.map((ew: any) => {
          let tables: number[] = [];
          try {
            tables = typeof ew.assignedTables === 'string'
              ? JSON.parse(ew.assignedTables)
              : (ew.assignedTables || []);
          } catch {}
          return {
            waiterId: ew.waiter?.id || ew.waiterId,
            waiterName: ew.waiter?.name || '',
            assignedTables: tables,
            pin: ''
          };
        }) || [];

        setEventData({
          name: event.name || '',
          estimatedGuests: event.estimatedGuests,
          activeTables,
          reserveTables,
          breaks,
          assignedWaiters,
          scheduledFor: event.scheduledFor ? event.scheduledFor.split('T')[0] : null
        });

        // Also update the table inputs to show current values
        setActiveTableInput(activeTables.join(','));
        setReserveTableInput(reserveTables.join(','));
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      alert('שגיאה בטעינת האירוע');
    } finally {
      setIsLoadingEvent(false);
    }
  };

  const loadAvailableData = async () => {
    try {
      // Load dishes and waiters from API in parallel
      const [dishesRes, waitersRes] = await Promise.all([
        fetch('/api/v1/dishes'),
        fetch('/api/v1/waiters')
      ]);

      const dishesData = await dishesRes.json();
      const waitersData = await waitersRes.json();

      if (dishesData.success) {
        setAvailableDishes(dishesData.dishes);
      }
      if (waitersData.success) {
        setAvailableWaiters(waitersData.waiters);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const loadPastEvents = async () => {
    setIsLoadingPastEvents(true);
    try {
      // Load both drafts and ended events for copying
      const response = await fetch('/api/v1/events?status=draft,ended');
      const data = await response.json();
      if (data.success && data.events) {
        // Exclude current event if in edit mode
        const events = eventId
          ? data.events.filter((e: any) => e.id !== eventId)
          : data.events;
        setPastEvents(events);
      }
    } catch (error) {
      console.error('Failed to load past events:', error);
    } finally {
      setIsLoadingPastEvents(false);
    }
  };

  const copyFromEvent = async (eventId: string) => {
    try {
      // Fetch the full event with breaks
      const response = await fetch(`/api/v1/events/${eventId}`);
      const data = await response.json();

      if (data.success && data.event) {
        const event = data.event;

        // Copy breaks and their dishes
        const copiedBreaks: EventBreak[] = event.breaks?.map((b: any) => ({
          id: Date.now().toString() + Math.random().toString(),
          name: b.name,
          dishes: b.dishes?.map((bd: any) => bd.dish).filter(Boolean) || []
        })) || [];

        // Update event data with copied structure
        setEventData(prev => ({
          ...prev,
          breaks: copiedBreaks,
          // Copy table count as active tables range
          activeTables: Array.from({ length: event.tableCount || 10 }, (_, i) => i + 1)
        }));

        setShowCopyModal(false);
        alert('המבנה הועתק בהצלחה! בדוק את השלבים הבאים.');
      }
    } catch (error) {
      console.error('Failed to copy event:', error);
      alert('שגיאה בהעתקת האירוע');
    }
  };

  const handleOpenCopyModal = () => {
    loadPastEvents();
    setShowCopyModal(true);
  };

  // Step 1: Basic Event Info - rendered inline to avoid re-mount on state change
  const renderBasicInfoStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          פרטי האירוע
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              שם האירוע *
            </label>
            <input
              type="text"
              value={eventData.name}
              onChange={(e) => setEventData(prev => ({ ...prev, name: e.target.value }))}
              maxLength={50}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="הזן שם לאירוע"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {eventData.name.length}/50 תווים
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              מספר אורחים משוער
            </label>
            <input
              type="number"
              value={eventData.estimatedGuests || ''}
              onChange={(e) => setEventData(prev => ({ ...prev, estimatedGuests: e.target.value ? parseInt(e.target.value) : null }))}
              min="1"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="הזן מספר אורחים משוער"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              תאריך האירוע (אופציונלי)
            </label>
            <input
              type="date"
              value={eventData.scheduledFor || ''}
              onChange={(e) => setEventData(prev => ({ ...prev, scheduledFor: e.target.value || null }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              התחל מתבנית קיימת (אופציונלי)
            </h4>
            <div className="space-y-2">
              <button
                onClick={handleOpenCopyModal}
                className="w-full text-left p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="font-medium text-gray-900 dark:text-white">העתק מאירוע קודם</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">העתק מבנה (ללא הקצאת מלצרים)</div>
              </button>
              <button
                disabled
                className="w-full text-left p-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg opacity-50 cursor-not-allowed"
              >
                <div className="font-medium text-gray-500 dark:text-gray-400">העתק מתבנית שמורה</div>
                <div className="text-sm text-gray-400 dark:text-gray-500">בקרוב...</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Parse table input - supports both comma-separated and range notation (e.g., "1-10")
  const parseTableInput = (input: string): number[] => {
    const result: number[] = [];
    const parts = input.split(',').map(p => p.trim()).filter(p => p);

    for (const part of parts) {
      // Check for range notation (e.g., "1-10")
      if (part.includes('-')) {
        const [startStr, endStr] = part.split('-').map(s => s.trim());
        const start = parseInt(startStr);
        const end = parseInt(endStr);
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            result.push(i);
          }
        }
      } else {
        const num = parseInt(part);
        if (!isNaN(num)) {
          result.push(num);
        }
      }
    }
    return result;
  };

  // Tables step handlers
  const addActiveTables = () => {
    const tables = parseTableInput(activeTableInput);
    const uniqueTables = [...new Set([...eventData.activeTables, ...tables])].sort((a, b) => a - b);
    setEventData({ ...eventData, activeTables: uniqueTables });
    setActiveTableInput('');
  };

  const addReserveTables = () => {
    const tables = parseTableInput(reserveTableInput);
    const uniqueTables = [...new Set([...eventData.reserveTables, ...tables])].sort((a, b) => a - b);
    setEventData({ ...eventData, reserveTables: uniqueTables });
    setReserveTableInput('');
  };

  const removeActiveTable = (table: number) => {
    setEventData({
      ...eventData,
      activeTables: eventData.activeTables.filter(t => t !== table)
    });
  };

  const removeReserveTable = (table: number) => {
    setEventData({
      ...eventData,
      reserveTables: eventData.reserveTables.filter(t => t !== table)
    });
  };

  // Step 2: Table Configuration - render function (not component) to prevent focus loss
  const renderTablesStep = () => (
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            הגדרת שולחנות
          </h3>

          {/* Active Tables */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                שולחנות פעילים
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={activeTableInput}
                  onChange={(e) => setActiveTableInput(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="הזן מספרי שולחנות (1,2,3 או 1-10)"
                />
                <button
                  onClick={addActiveTables}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  הוסף
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                הזן מספרים מופרדים בפסיק (למשל: 1,2,3,4,5)
              </p>

              {eventData.activeTables.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {eventData.activeTables.map(table => (
                    <span
                      key={table}
                      className="inline-flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm"
                    >
                      שולחן {table}
                      <button
                        onClick={() => removeActiveTable(table)}
                        className="mr-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Reserve Tables */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                שולחנות רזרבה (סטנדביי)
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={reserveTableInput}
                  onChange={(e) => setReserveTableInput(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="הזן מספרי שולחנות (1,2,3 או 1-10)"
                />
                <button
                  onClick={addReserveTables}
                  className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  הוסף
                </button>
              </div>

              {eventData.reserveTables.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {eventData.reserveTables.map(table => (
                    <span
                      key={table}
                      className="inline-flex items-center bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 px-3 py-1 rounded-full text-sm"
                    >
                      רזרבה {table}
                      <button
                        onClick={() => removeReserveTable(table)}
                        className="mr-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );

  // Breaks step handlers
  const addBreak = () => {
    if (newBreakName.trim()) {
      const newBreak: EventBreak = {
        id: Date.now().toString(),
        name: newBreakName.trim(),
        dishes: []
      };
      setEventData({
        ...eventData,
        breaks: [...eventData.breaks, newBreak]
      });
      setNewBreakName('');
      setShowAddBreak(false);
    }
  };

  const removeBreak = (breakId: string) => {
    setEventData({
      ...eventData,
      breaks: eventData.breaks.filter(b => b.id !== breakId)
    });
  };

  const addDishToBreak = (breakId: string, dish: Dish) => {
    setEventData({
      ...eventData,
      breaks: eventData.breaks.map(eventBreak =>
        eventBreak.id === breakId
          ? { ...eventBreak, dishes: [...eventBreak.dishes, dish] }
          : eventBreak
      )
    });
  };

  const removeDishFromBreak = (breakId: string, dishId: string) => {
    setEventData({
      ...eventData,
      breaks: eventData.breaks.map(eventBreak =>
        eventBreak.id === breakId
          ? { ...eventBreak, dishes: eventBreak.dishes.filter(d => d.id !== dishId) }
          : eventBreak
      )
    });
  };

  // Step 3: Breaks Configuration - render function (not component) to prevent focus loss
  const renderBreaksStep = () => (
      <div className="space-y-6">
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              הגדרת מנות
            </h3>
            <button
              onClick={() => setShowAddBreak(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              הוסף מנה
            </button>
          </div>

          {/* Add Break Form */}
          {showAddBreak && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={newBreakName}
                  onChange={(e) => setNewBreakName(e.target.value)}
                  placeholder="שם המנה (למשל: מנה ראשונה, עיקרית, קינוח)"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                />
                <div className="flex gap-3">
                  <button
                    onClick={addBreak}
                    className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    הוסף
                  </button>
                  <button
                    onClick={() => {
                      setShowAddBreak(false);
                      setNewBreakName('');
                    }}
                    className="flex-1 sm:flex-none bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Breaks List */}
          {eventData.breaks.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-gray-600 dark:text-gray-300">
                טרם הוגדרו מנות לאירוע
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {eventData.breaks.map((eventBreak) => (
                <div key={eventBreak.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {eventBreak.name}
                    </h4>
                    <button
                      onClick={() => removeBreak(eventBreak.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                    >
                      מחק מנה
                    </button>
                  </div>

                  {/* Dishes in Break */}
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      מנות ({eventBreak.dishes.length}/10):
                    </p>

                    {eventBreak.dishes.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        לא נבחרו מנות
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {eventBreak.dishes.map(dish => (
                          <span
                            key={dish.id}
                            className="inline-flex items-center bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm"
                          >
                            {dish.name}
                            <button
                              onClick={() => removeDishFromBreak(eventBreak.id, dish.id)}
                              className="mr-2 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Add Dish Selector */}
                    {eventBreak.dishes.length < 10 && (
                      <div className="mt-3">
                        <select
                          onChange={(e) => {
                            const dish = availableDishes.find(d => d.id === e.target.value);
                            if (dish && !eventBreak.dishes.find(d => d.id === dish.id)) {
                              addDishToBreak(eventBreak.id, dish);
                            }
                          }}
                          value=""
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">בחר מנה להוספה...</option>
                          {availableDishes
                            .filter(dish => !eventBreak.dishes.find(d => d.id === dish.id))
                            .map(dish => (
                              <option key={dish.id} value={dish.id}>
                                {dish.name}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
  );

  // Waiters step handlers
  const addWaiter = () => {
    if (selectedWaiterId && selectedWaiterTables.length > 0) {
      const waiter = availableWaiters.find(w => w.id === selectedWaiterId);
      if (waiter) {
        const newAssignment: AssignedWaiter = {
          waiterId: waiter.id,
          waiterName: waiter.name,
          assignedTables: [...selectedWaiterTables].sort((a, b) => a - b),
          pin: '' // PIN will be generated by backend
        };
        setEventData({
          ...eventData,
          assignedWaiters: [...eventData.assignedWaiters, newAssignment]
        });
        setSelectedWaiterId('');
        setSelectedWaiterTables([]);
        setShowAddWaiter(false);
      }
    }
  };

  // Toggle table selection for waiter
  const toggleWaiterTable = (table: number) => {
    setSelectedWaiterTables(prev =>
      prev.includes(table)
        ? prev.filter(t => t !== table)
        : [...prev, table]
    );
  };

  // Get tables that are already assigned to other waiters
  const getAssignedTables = (): number[] => {
    return eventData.assignedWaiters.flatMap(w => w.assignedTables);
  };

  const removeWaiter = (waiterId: string) => {
    setEventData({
      ...eventData,
      assignedWaiters: eventData.assignedWaiters.filter(w => w.waiterId !== waiterId)
    });
  };

  // Step 4: Waiter Assignment - render function (not component) to prevent focus loss
  const renderWaitersStep = () => (
      <div className="space-y-6">
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              הקצאת מלצרים
            </h3>
            <button
              onClick={() => setShowAddWaiter(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              הוסף מלצר
            </button>
          </div>

          {/* Add Waiter Form */}
          {showAddWaiter && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    בחר מלצר
                  </label>
                  <select
                    value={selectedWaiterId}
                    onChange={(e) => setSelectedWaiterId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">בחר מלצר...</option>
                    {availableWaiters
                      .filter(waiter => !eventData.assignedWaiters.find(aw => aw.waiterId === waiter.id))
                      .map(waiter => (
                        <option key={waiter.id} value={waiter.id}>
                          {waiter.name}
                        </option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    בחר שולחנות (לחץ לבחירה)
                  </label>
                  {eventData.activeTables.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                      לא הוגדרו שולחנות פעילים - חזור לשלב השולחנות
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {eventData.activeTables.map((table) => {
                        const isAssigned = getAssignedTables().includes(table);
                        const isSelected = selectedWaiterTables.includes(table);
                        return (
                          <button
                            key={table}
                            type="button"
                            onClick={() => !isAssigned && toggleWaiterTable(table)}
                            disabled={isAssigned}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              isAssigned
                                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                : isSelected
                                ? 'bg-blue-600 text-white'
                                : 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {table}
                            {isAssigned && <span className="mr-1 text-xs">(תפוס)</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedWaiterTables.length > 0 && (
                    <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                      נבחרו: {selectedWaiterTables.sort((a, b) => a - b).join(', ')}
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={addWaiter}
                    disabled={!selectedWaiterId || selectedWaiterTables.length === 0}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    הוסף מלצר
                  </button>
                  <button
                    onClick={() => {
                      setShowAddWaiter(false);
                      setSelectedWaiterId('');
                      setSelectedWaiterTables([]);
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Assigned Waiters */}
          {eventData.assignedWaiters.length === 0 ? (
            <div className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-yellow-800 dark:text-yellow-300 font-medium">
                לא הוקצו מלצרים לאירוע
              </p>
              <p className="text-yellow-600 dark:text-yellow-400 text-sm mt-1">
                ניתן להמשיך ולשמור - יש להקצות מלצרים לפני התחלת האירוע
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {eventData.assignedWaiters.map((assignedWaiter) => (
                <div key={assignedWaiter.waiterId} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {assignedWaiter.waiterName}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        שולחנות: {assignedWaiter.assignedTables.join(', ')}
                      </p>
                    </div>
                    <button
                      onClick={() => removeWaiter(assignedWaiter.waiterId)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                    >
                      הסר
                    </button>
                  </div>
                </div>
              ))}
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-4">
                קודי הגישה יוצגו בסיום יצירת האירוע
              </p>
            </div>
          )}
        </div>
      </div>
  );

  // Step 5: Review - render function (not component) to prevent focus loss
  const renderReviewStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          סיכום האירוע
        </h3>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">פרטי האירוע</h4>
            <p className="text-gray-600 dark:text-gray-300">שם: {eventData.name}</p>
          </div>

          {/* Tables */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">שולחנות</h4>
            <div className="space-y-1">
              <p className="text-gray-600 dark:text-gray-300">
                פעילים ({eventData.activeTables.length}): {eventData.activeTables.join(', ') || 'אין'}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                רזרבה ({eventData.reserveTables.length}): {eventData.reserveTables.join(', ') || 'אין'}
              </p>
            </div>
          </div>

          {/* Breaks */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">מנות ({eventData.breaks.length})</h4>
            {eventData.breaks.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-300">אין מנות מוגדרות</p>
            ) : (
              <div className="space-y-2">
                {eventData.breaks.map(eventBreak => (
                  <div key={eventBreak.id}>
                    <p className="text-gray-600 dark:text-gray-300">
                      {eventBreak.name}: {eventBreak.dishes.length} מנות
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Waiters */}
          <div className={`border rounded-lg p-4 ${eventData.assignedWaiters.length === 0 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600'}`}>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">מלצרים ({eventData.assignedWaiters.length})</h4>
            {eventData.assignedWaiters.length === 0 ? (
              <div>
                <p className="text-yellow-800 dark:text-yellow-300 font-medium">אין מלצרים מוקצים</p>
                <p className="text-yellow-600 dark:text-yellow-400 text-sm mt-1">
                  יש להקצות מלצרים לפני התחלת האירוע
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {eventData.assignedWaiters.map(waiter => (
                  <p key={waiter.waiterId} className="text-gray-600 dark:text-gray-300">
                    {waiter.waiterName}: שולחנות {waiter.assignedTables.join(', ')}
                  </p>
                ))}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  קודי הגישה יופקו בעת התחלת האירוע
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const handleNext = () => {
    const steps: Step[] = ['basic', 'tables', 'breaks', 'waiters', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handlePrevious = () => {
    const steps: Step[] = ['basic', 'tables', 'breaks', 'waiters', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleSaveEvent = async () => {
    setIsLoading(true);
    try {
      // Build event payload
      const eventPayload = {
        name: eventData.name,
        tableCount: eventData.activeTables.length + eventData.reserveTables.length,
        estimatedGuests: eventData.estimatedGuests,
        reserveTables: eventData.reserveTables,
        scheduledFor: eventData.scheduledFor,
        breaks: eventData.breaks.map(b => ({
          name: b.name,
          dishes: b.dishes.map(d => d.id)
        }))
      };

      let savedEventId: string;

      if (mode === 'edit' && eventId) {
        // Update existing event
        const eventResponse = await fetch(`/api/v1/events/${eventId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(eventPayload)
        });

        const eventResult = await eventResponse.json();
        if (!eventResult.success) {
          throw new Error(eventResult.error || 'Failed to update event');
        }
        savedEventId = eventId;
      } else {
        // Create new event (as draft)
        const eventResponse = await fetch('/api/v1/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(eventPayload)
        });

        const eventResult = await eventResponse.json();
        if (!eventResult.success) {
          throw new Error(eventResult.error || 'Failed to create event');
        }
        savedEventId = eventResult.event.id;
      }

      // Assign waiters to the event (no PINs generated for drafts)
      for (const waiter of eventData.assignedWaiters) {
        await fetch(`/api/v1/waiters/${waiter.waiterId}/assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventId: savedEventId,
            assignedTables: waiter.assignedTables
          })
        });
      }

      // No PINs modal - just complete
      onComplete();
    } catch (error) {
      console.error('Failed to save event:', error);
      alert('שגיאה בשמירת האירוע');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'basic':
        return eventData.name.trim().length > 0;
      case 'tables':
        return eventData.activeTables.length > 0;
      case 'breaks':
        return eventData.breaks.length > 0;
      case 'waiters':
        // Waiters are optional - can proceed without assigning
        return true;
      default:
        return true;
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'basic':
        return renderBasicInfoStep();
      case 'tables':
        return renderTablesStep();
      case 'breaks':
        return renderBreaksStep();
      case 'waiters':
        return renderWaitersStep();
      case 'review':
        return renderReviewStep();
      default:
        return renderBasicInfoStep();
    }
  };

  // Show loading state when loading event data for edit
  if (isLoadingEvent) {
    return (
      <div className="max-w-4xl mx-auto p-3 sm:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">טוען נתוני אירוע...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="hidden sm:inline">{mode === 'edit' ? 'חזור לאירועים שמורים' : 'חזור ללוח הבקרה'}</span>
          <span className="sm:hidden">חזור</span>
        </button>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {mode === 'edit' ? 'עריכת אירוע' : 'יצירת אירוע חדש'}
        </h2>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6">
        {/* Progress Steps */}
        <div className="mb-6 sm:mb-8">
          <div className="flex justify-between items-center">
            {[
              { key: 'basic', label: 'פרטי האירוע', shortLabel: 'פרטים' },
              { key: 'tables', label: 'שולחנות', shortLabel: 'שולחנות' },
              { key: 'breaks', label: 'מנות', shortLabel: 'מנות' },
              { key: 'waiters', label: 'מלצרים', shortLabel: 'מלצרים' },
              { key: 'review', label: 'סיכום', shortLabel: 'סיכום' }
            ].map((step, index) => (
              <div key={step.key} className="flex flex-col sm:flex-row items-center flex-1">
                <div className="flex items-center">
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                      currentStep === step.key
                        ? 'bg-blue-600 text-white'
                        : ['basic', 'tables', 'breaks', 'waiters', 'review'].indexOf(currentStep) > index
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {['basic', 'tables', 'breaks', 'waiters', 'review'].indexOf(currentStep) > index ? '✓' : index + 1}
                  </div>
                  {index < 4 && (
                    <div className={`w-4 sm:w-8 h-0.5 mx-1 sm:mx-2 sm:hidden ${
                      ['basic', 'tables', 'breaks', 'waiters', 'review'].indexOf(currentStep) > index
                        ? 'bg-green-600'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`} />
                  )}
                </div>
                <span className={`hidden sm:block mr-2 text-sm ${
                  currentStep === step.key ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                }`}>
                  {step.label}
                </span>
                <span className={`sm:hidden mt-1 text-[10px] text-center ${
                  currentStep === step.key ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {step.shortLabel}
                </span>
                {index < 4 && (
                  <div className={`hidden sm:block w-8 h-0.5 mx-2 ${
                    ['basic', 'tables', 'breaks', 'waiters', 'review'].indexOf(currentStep) > index
                      ? 'bg-green-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-6 sm:mb-8">
          {renderCurrentStep()}
        </div>

        {/* Navigation */}
        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 'basic'}
            className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 sm:py-2 rounded-lg transition-colors"
          >
            קודם
          </button>

          <div className="flex gap-3">
            {currentStep === 'review' ? (
              <button
                onClick={handleSaveEvent}
                disabled={isLoading || !canProceed()}
                className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-8 py-3 sm:py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {isLoading && (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                )}
                {mode === 'edit' ? 'שמור שינויים' : 'שמור אירוע'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 sm:py-2 rounded-lg transition-colors"
              >
                הבא
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Copy from Previous Event Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                בחר אירוע להעתקה
              </h3>
              <button
                onClick={() => setShowCopyModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {isLoadingPastEvents ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">טוען אירועים...</p>
                </div>
              ) : pastEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>אין אירועים קודמים להעתקה</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pastEvents.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => copyFromEvent(event.id)}
                      className="w-full text-right p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {event.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {event.tableCount} שולחנות • {event.breaks?.length || 0} מנות
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(event.createdAt).toLocaleDateString('he-IL')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EventCreator;